import { Button, MessageBar, MessageBarBody } from "@fluentui/react-components";
import type { ApprovalRequest } from "../types";
import { CheckIcon } from "../../shared/components/icons";
import styles from "./ApprovalCard.module.css";

interface ApprovalCardProps {
  readonly approval: ApprovalRequest;
  /** Disables the buttons while a decision is in flight. */
  readonly busy?: boolean;
  readonly onDecision: (decision: "approve" | "reject") => void | Promise<void>;
}

/** Human-friendly heading for a gated tool, e.g. "Send email". */
function actionTitle(tool: string, args?: Record<string, unknown>): string {
  // manage_calendar spans several actions with very different consequences, so
  // the heading comes from the action rather than the tool name.
  if (tool === "manage_calendar") {
    const byAction: Record<string, string> = {
      update_event: "Change calendar event",
      create_event: "Add calendar event",
      respond_to_event: "Respond to invitation",
    };
    // Only a string action can name a heading; anything else falls back.
    const action = typeof args?.action === "string" ? args.action : "";
    return byAction[action] ?? "Calendar change";
  }
  const titles: Record<string, string> = {
    submit_email_draft: "Send email",
    send_email_with_attachment: "Send email with attachment",
    create_compose_draft: "Create email draft with attachment",
    create_reply_draft: "Create reply draft with attachment",
    submit_meeting_draft: "Create meeting",
    submit_teams_meeting_draft: "Create Teams meeting",
    send_docusign_envelope: "Send DocuSign envelope",
  };
  return titles[tool] ?? tool.replace(/_/g, " ");
}

/**
 * Args that are internal plumbing or opaque ids — never useful to a reviewer,
 * and actively harmful on the card: `event_id` is a ~150-char base64 blob that
 * pushed the real details off screen (AIVA-6391).
 *
 * Deliberately NOT hidden: `override_conflict`, because it changes what the
 * user is consenting to (booking over an existing event).
 */
const HIDDEN_ARGS = new Set([
  "action",
  "event_id",
  "message_id",
  "draft_id",
  "conversation_id",
  "verified_slot_id",
  "time_source",
  "time_quote",
  "file_numbers",
  "body_format",
  "kind",
  "mode",
  "include_weekends",
  // SAF-3: consumed below to stand in for the inert to/cc on a reply, never
  // rendered under their own raw key. Listed here too as a safety net — if
  // they ever arrive outside a reply/reply_all draft (they shouldn't), a
  // literal "resolved to" row is worse than silently dropping it.
  "resolved_to",
  "resolved_cc",
]);

/** Naive or zoned ISO datetime, e.g. 2026-07-29T16:30:00. */
const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?/;

/**
 * The interrupt middleware's default description: a prose header followed by the
 * whole tool-call dict. It re-dumps every argument the card renders properly
 * below — including the ones HIDDEN_ARGS exists to keep off screen (a ~150-char
 * base64 `message_id`) and an email body with its raw HTML tags. A tool that
 * wants a description supplies a purposeful one (see the calendar gate's
 * `build_calendar_gate_description`); the default is noise and is dropped.
 */
const DEFAULT_GATE_DESCRIPTION = /^\s*Tool execution requires approval/i;

const EXTERNAL_WARNING = /outside your organisation/i;


function splitExternalWarning(description: string): { warning: string | null; rest: string } {
  const [first, ...others] = description.split(/\n\s*\n/);
  if (!first || others.length === 0 || !EXTERNAL_WARNING.test(first)) return { warning: null, rest: description };
  return { warning: first.trim(), rest: others.join("\n\n").trim() };
}

/** True when *text* looks like markup rather than something a person typed. */
const HTML_TAG = /<\/?[a-z][^>]*>/i;

const HTML_ENTITIES: Record<string, string> = {
  "&nbsp;": " ",
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
  "&apos;": "'",
};

/**
 * Flatten an HTML fragment to readable text for the card.
 *
 * Email bodies reach the gate as HTML (`<p>Hi Abigail,</p><p>Thanks …`), and the
 * body is the single most important thing to review before approving a send —
 * showing it with the tags intact makes the one field that matters the hardest
 * to read. Block ends become line breaks so paragraphs survive.
 */
function htmlToText(value: string): string {
  if (!HTML_TAG.test(value)) return value;
  let text = value
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<li[^>]*>/gi, "\n• ")
    .replace(/<\/(p|div|li|h[1-6]|tr|blockquote)\s*>/gi, "\n")
    .replace(/<[^<>]+>/g, "");
  for (const [entity, char] of Object.entries(HTML_ENTITIES)) {
    text = text.split(entity).join(char);
  }
  return text
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Render one argument value compactly (arrays joined, objects summarised). */
function formatArgValue(value: unknown): string {
  if (value == null) return "";
  // Recurse per element (main's AIVA-6436 shape) so each one gets the same HTML
  // flattening and date formatting as a scalar, and blanks are dropped rather
  // than rendering as a dangling ", ".
  if (Array.isArray(value)) return value.map(formatArgValue).filter(Boolean).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  // Narrowed explicitly so String() provably cannot stringify a non-primitive.
  // Functions/symbols/bigints never appear in a JSON tool-args payload.
  if (typeof value !== "string" && typeof value !== "number" && typeof value !== "boolean") return "";
  const str = htmlToText(String(value));
  // Raw ISO timestamps are unreadable on a card the user is asked to approve.
  if (ISO_DATETIME.test(str)) {
    const dt = new Date(str);
    if (!Number.isNaN(dt.getTime())) {
      return dt.toLocaleString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
        hour: "numeric",
        minute: "2-digit",
      });
    }
  }
  return str;
}

const MAX_ARG_CHARS = 280;

/**
 * `submit_email_draft` modes where `to`/`cc` are inert (SAF-3).
 *
 * On `reply` / `reply_all`, Graph derives the actual recipients from the
 * original message — `_apply_graph_threading` in the AI backend receives only
 * `message_id`, `body` and `mode`, never `to`/`cc` — so those two fields are
 * whatever the model happened to fill in, with no bearing on who the mail
 * reaches. Rendering them on the approval card shows the reviewer a value
 * nothing ever used, right next to the gate's own external-recipient warning
 * (built from the REAL resolved recipients), which can name someone else
 * entirely. Compose mode is unaffected: there `to`/`cc` ARE the Graph
 * arguments.
 */
const REPLY_MODES_WITH_INERT_RECIPIENTS = new Set(["reply", "reply_all"]);

/**
 * Swap a reply/reply_all draft's inert `to`/`cc` for the REAL resolved
 * recipients the backend attaches as `resolved_to`/`resolved_cc` (SAF-3
 * backend follow-up — `_enrich_approval_required` in the AI backend's
 * `API/routes/v2/stream.py`, sourced from the same `RecipientIndex` the
 * external-recipient warning sentence already uses).
 *
 * Falls back to showing NO recipients — never the inert ones — when the
 * backend could not resolve them (an unknown reply target) or predates this
 * enrichment (older cached/replayed events). A wrong address is worse than no
 * address; that was the whole bug.
 */
function withResolvedRecipients(args: Record<string, unknown>): Record<string, unknown> {
  const mode = typeof args.mode === "string" ? args.mode : undefined;
  if (!mode || !REPLY_MODES_WITH_INERT_RECIPIENTS.has(mode)) return args;
  const display = { ...args };
  delete display.to;
  delete display.cc;
  if (Array.isArray(args.resolved_to) && args.resolved_to.length > 0) display.to = args.resolved_to;
  if (Array.isArray(args.resolved_cc) && args.resolved_cc.length > 0) display.cc = args.resolved_cc;
  return display;
}

/**
 * In-chat approval card for a HITL-gated action (chat v2, AIVA-5929).
 * The run is paused server-side; Approve/Deny resumes it via
 * POST /chat/resume with kind="approval". After a decision the card stays
 * visible but disabled, stamped with the outcome.
 */
export function ApprovalCard({ approval, busy = false, onDecision }: Readonly<ApprovalCardProps>) {
  const decided = approval.decision !== undefined;
  const stale = approval.stale === true && !decided;
  const disabled = decided || busy || stale;
  // Hide internal args (ours), swap a reply's inert to/cc for the real resolved
  // recipients when the backend sent them (SAF-3), then drop anything that
  // FORMATS to nothing — main's AIVA-6436 test, and stricter than checking the
  // raw value: `cc: [""]` renders as an empty row under a raw-value check but
  // is caught here.
  const displayArgs = withResolvedRecipients(approval.args ?? {});
  const args = Object.entries(displayArgs).filter(
    ([k, v]) => !HIDDEN_ARGS.has(k) && formatArgValue(v) !== "",
  );
  const { warning, rest: description } =
    approval.description && !DEFAULT_GATE_DESCRIPTION.test(approval.description)
      ? splitExternalWarning(approval.description)
      : { warning: null, rest: null };

  return (
    <fieldset className={`${styles.card} ${stale ? styles.stale : ""}`} aria-label="Approval required">
      <div className={styles.title}>Approval required — {actionTitle(approval.tool, approval.args)}</div>
      {warning && (
        // Multiline: the default single-line layout never wraps, so in the narrow
        // task pane a long sentence ran straight out of the card instead of the
        // bar growing taller.
        <MessageBar intent="warning" layout="multiline" className={styles.warning}>
          <MessageBarBody className={styles.warningBody}>{warning}</MessageBarBody>
        </MessageBar>
      )}
      {description && <div className={styles.description}>{description}</div>}
      {args.length > 0 && (
        <dl className={styles.args}>
          {args.map(([key, value]) => {
            const rendered = formatArgValue(value);
            return (
              <div key={key} className={styles.argRow}>
                <dt className={styles.argKey}>{key.replace(/_/g, " ")}</dt>
                <dd className={styles.argValue}>
                  {rendered.length > MAX_ARG_CHARS ? `${rendered.slice(0, MAX_ARG_CHARS)}…` : rendered}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
      <div className={styles.actions}>
        <Button appearance="primary" size="small" disabled={disabled} onClick={() => onDecision("approve")}>
          Approve
        </Button>
        <Button appearance="secondary" size="small" disabled={disabled} onClick={() => onDecision("reject")}>
          Deny
        </Button>
        {decided && (
          <span className={styles.decisionStamp}>
            {approval.decision === "approve" ? <>Approved <CheckIcon /></> : "Denied"}
          </span>
        )}
        {stale && (
          <span className={styles.decisionStamp}>Not needed anymore — just ask again if you still want this.</span>
        )}
      </div>
    </fieldset>
  );
}
